import { FiX } from "react-icons/fi";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { IdxFeedTypeEnum } from "dash-common";

// Form Schema with zod
const NewIdxFeedFormSchema = z.object({
  name: z.string().min(1, "Name is required."),
  type: z.nativeEnum(IdxFeedTypeEnum, {
    errorMap: () => ({ message: "Type is required." }),
  }),
});

// Types
export type NewIdxFeedFormData = z.infer<typeof NewIdxFeedFormSchema>;
type NewIdxFeedModalProps = {
  onClose?: () => void;
  onSave?: (data: NewIdxFeedFormData) => Promise<void>;
};

// Main Component
export function NewIdxFeedModal(props: NewIdxFeedModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  //Hooks
  const idxSettingsForm = useForm<NewIdxFeedFormData>({
    resolver: zodResolver(NewIdxFeedFormSchema),
  });

  // Function to handle save
  async function save(data: NewIdxFeedFormData) {
    setLoading(true);
    if (props.onSave) await props.onSave(data);
    if (props.onClose) props.onClose();
    setLoading(false);
  }

  // HTML Component
  return (
    <div className="fixed w-screen h-screen bg-black/30 top-0 left-0 z-10 flex items-center justify-center">
      <form
        onSubmit={idxSettingsForm.handleSubmit(save)}
        className="bg-white shadow-2xl rounded-lg p-8 flex flex-col w-full max-w-screen-sm gap-6 relative"
      >
        <button
          type="button"
          onClick={props.onClose}
          className="absolute size-8 flex items-center justify-center rounded-lg hover:bg-black/5 ml-auto top-4 right-4"
        >
          <FiX />
        </button>
        <header className="flex">
          <h1 className="font-bold">New Exchange Feed</h1>
        </header>
        <main className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-slate-500">Name*</label>
            <input
              type="text"
              className={`h-10 px-4 border ${
                idxSettingsForm.formState.errors.name ? "border-red-600" : "border-slate-300"
              } rounded-lg`}
              placeholder="Enter a name"
              {...idxSettingsForm.register("name")}
            />
            {idxSettingsForm.formState.errors.name && (
              <span className="text-sm text-red-700">
                {idxSettingsForm.formState.errors.name.message}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm text-slate-500">Type*</label>
            <select
              defaultValue=""
              className={`h-10 px-4 border ${
              idxSettingsForm.formState.errors.type ? "border-red-600" : "border-slate-300"
              } rounded-lg`}
              {...idxSettingsForm.register("type")}
            >
              <option value="" disabled hidden>
              Select a type
              </option>
              <option value={IdxFeedTypeEnum.SINGLE_PROPERTY}>Single Property</option>
              <option value={IdxFeedTypeEnum.PORTFOLIO}>Portfolio</option>
              <option value={IdxFeedTypeEnum.SEARCH_BAR}>Search Bar</option>
            </select>
            {idxSettingsForm.formState.errors.type && (
              <span className="text-sm text-red-700">
                {idxSettingsForm.formState.errors.type.message}
              </span>
            )}
          </div>
        </main>
        <footer className="flex gap-4 justify-end">
          <button
            type="submit"
            className="h-10 px-4 rounded-lg bg-sky-600 text-white hover:brightness-110 disabled:opacity-30"
            disabled={loading}
          >
            Create
          </button>
          <button
            type="reset"
            onClick={props.onClose}
            className="h-10 border border-slate-300 px-4 rounded-lg hover:bg-black/5"
          >
            Cancel
          </button>
        </footer>
      </form>
    </div>
  );
}
